import ApiClient from '../config/ApiClient';
import { CoachContent } from '../object-actions/types/types';

/**
 * Publish CoachContent (mark as delivered)
 */
export const publishCoachContent = async (coachContentId: number): Promise<CoachContent> => {
    try {
        const response = await ApiClient.post(`/api/coach-content/${coachContentId}/publish/`, {});

        if (response.error) {
            throw new Error(response.error);
        }

        return response.data as CoachContent;
    } catch (error) {
        console.error('Error publishing coach content:', error);
        throw error;
    }
};

/**
 * Regenerate CoachContent with changes
 */
export const regenerateWithChanges = async (
    coachContentId: number,
    changeRequest: string
): Promise<CoachContent> => {
    try {
        const response = await ApiClient.post(`/api/coach-content/${coachContentId}/regenerate_with_changes/`, {
            change_request: changeRequest
        });

        if (response.error) {
            throw new Error(response.error);
        }

        return response.data as CoachContent;
    } catch (error) {
        console.error('Error regenerating with changes:', error);
        throw error;
    }
};

/**
 * Get version history for CoachContent
 */
export const getVersionHistory = async (
    coachContentId: number | string,
    athleteId: number,
    purpose: string,
    assessmentId?: number
): Promise<CoachContent[]> => {
    // TODO: get actual past versions of this content not just other ones for this athlete and purpose

    try {
        const params = new URLSearchParams({
            id: coachContentId.toString(),
            athlete: athleteId.toString(),
            purpose,
            ...(assessmentId && { assessment: assessmentId.toString() })
        });

        const response = await ApiClient.get(`/api/coach-content/?${params}`);

        if (response.error) {
            throw new Error(response.error);
        }

        return (response.data as any).results || [];
    } catch (error) {
        console.error('Error getting version history:', error);
        throw error;
    }
};

/**
 * Check if CoachContent is published (has coach_delivered timestamp)
 */
export const isContentPublished = (coachContent: CoachContent): boolean => {
    return !!coachContent.coach_delivered;
};

/**
 * Get delivery status for CoachContent
 */
export const getDeliveryStatus = (coachContent: CoachContent) => {
    return {
        coachDelivered: !!coachContent.coach_delivered,
        athleteReceived: !!coachContent.athlete_received,
        parentReceived: !!coachContent.parent_received,
        isPublished: isContentPublished(coachContent)
    };
};

/**
 * Format delivery timestamp for display
 */
export const formatDeliveryTimestamp = (timestamp: string | null): string => {
    if (!timestamp) return 'Not delivered';

    const date = new Date(timestamp);
    return date.toLocaleString();
};

/**
 * Mark CoachContent as received by athlete
 */
export const markAthleteReceived = async (coachContentId: number): Promise<CoachContent> => {
    try {
        const response = await ApiClient.post(`/api/coach-content/${coachContentId}/mark_athlete_received`, {});

        if (response.error) {
            throw new Error(response.error);
        }

        return response.data as CoachContent;
    } catch (error) {
        console.error('Error marking athlete received:', error);
        throw error;
    }
};

/**
 * Mark CoachContent as received by parent
 */
export const markParentReceived = async (coachContentId: number): Promise<CoachContent> => {
    try {
        const response = await ApiClient.post(`/api/coach-content/${coachContentId}/mark_parent_received`, {});

        if (response.error) {
            throw new Error(response.error);
        }

        return response.data as CoachContent;
    } catch (error) {
        console.error('Error marking parent received:', error);
        throw error;
    }
};
